<template>
  <div style="height: 100%">
    <GmapMap
      :center="center"
      :zoom="zoom"
      map-type-id="roadmap"
      style="width: 100%; height: 100%"
      :options="{ styles: settings }"
      ref="map">
      <template v-for="marker in markers">
        <GmapMarker
          draggable
          :key="marker.id + 'marker'"
          :position="marker.location"
          :label="showLabels ? { text: `${marker.denumire}`, fontSize, fontWeight, color: fontColor } : null"
          :icon="getIcon(marker)"
          @dragend="markerDragged($event, marker)"
          />
      </template>
      <template v-for="(poly, index) in polys">
        <GmapPolygon
          :ref="`poly-${index}`"
          v-if="showBorders"
          :key="index"
          :path="decode(poly.path)"
          :options="poly.options"
          :draggable="draggablePolys"
          @dragend="polyDragged($event, poly, index)"
          :editable="editablePolys" />

        <GmapMarker
          v-if="showLabels"
          :key="index+'m'"
          draggable
          @dragend="polyMarkerDragged($event, poly)"
          :position="poly.center || getCenter(poly)"
          :label="{ text: poly.label, fontSize: String(poly.options.fontSize) + 'px', fontWeight: String(poly.options.fontWeight), color: poly.options.fontColor }"
          :icon="{
            path: poly.options.markerPath,
            anchor: { x: 250, y: 500 },
            labelOrigin: { x: 250, y: 600 },
            fillColor: poly.options.labelFillColor,
            fillOpacity: poly.options.labelFillOpacity,
            strokeWeight: poly.options.labelStrokeWeight,
            strokeColor: poly.options.labelStrokeColor,
            scale: poly.options.labelScale
          }" />"
      </template>
    </GmapMap>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import * as html2canvas from 'html2canvas'
import saveAs from 'file-saver'

export default {
  name: 'laura-map',

  props: {
    resize: {
      type: Boolean
    },
    zoom: {
      type: Number,
      default: 11
    },
    labelX: {
      type: Number,
      default: 13
    },
    labelY: {
      type: Number,
      default: 26
    },
    fontColor: {
      type: String,
      default: '#000000'
    },
    fontSize: {
      type: String,
      default: '14px'
    },
    fontWeight: {
      type: String,
      default: '400'
    },
    iconStrokeWeight: {
      type: Number
    },
    iconOpacity: {
      type: Number
    },
    iconScale: {
      type: Number
    },
    icons: {
      type: Object
    },
    showBorders: {
      type: Boolean,
      default: true
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    settings: {
      // type: Object,
      default: () => ({})
    },
    markers: {
      type: Array,
      default: () => []
    },
    polys: {
      type: Array,
      default: () => []
    },
    draggablePolys: {
      type: Boolean,
      default: false
    },
    editablePolys: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    center: {
      lat: 44.4379268,
      lng: 26.0244263
    },
    poly: null
  }),

  watch: {
    resize (value) {
      this.$refs.map.resizePreserveCenter()
    },

    editablePolys: {
      immediate: false,

      handler (value) {
        if (!value) {
          for (let i = 0; i < this.polys.length; i++) {
            this.polyDragged(null, null, i)
          }
        }
      }
    }
  },

  computed: {
    google: gmapApi,
    getIcon (marker) {
      return marker => {
        const path = 'M 11.46875 1.7226562 A 7 7 0 0 0 4.46875 8.7226562 C 4.46875 13.972656 11.46875 21.722656 11.46875 21.722656 C 11.46875 21.722656 18.46875 13.972656 18.46875 8.7226562 A 7 7 0 0 0 11.46875 1.7226562 z M 10.171875 3.7246094 L 12.960938 3.7246094 L 12.960938 5.1171875 L 14.353516 5.1171875 C 14.864556 5.1171875 15.283203 5.531185 15.283203 6.046875 L 15.283203 8.1933594 L 15.882812 8.3886719 C 16.003603 8.4258319 16.101709 8.5095938 16.162109 8.6210938 C 16.213219 8.7325937 16.226623 8.8623656 16.189453 8.9785156 L 15.306641 12.085938 L 15.283203 12.085938 C 14.539863 12.085938 13.890371 11.67659 13.425781 11.15625 C 12.961201 11.67658 12.309746 12.085938 11.566406 12.085938 C 10.823066 12.085938 10.173574 11.67659 9.7089844 11.15625 C 9.2444041 11.67658 8.5929494 12.085938 7.8496094 12.085938 L 7.8222656 12.085938 L 6.9433594 8.9785156 C 6.9061994 8.8623656 6.9215563 8.7325937 6.9726562 8.6210938 C 7.0330462 8.5095938 7.12921 8.4258419 7.25 8.3886719 L 7.8496094 8.1933594 L 7.8496094 6.046875 A 0.92917 0.92917 0 0 1 8.7792969 5.1171875 L 10.171875 5.1171875 L 10.171875 3.7246094 z M 8.7792969 6.046875 L 8.7792969 7.8867188 L 11.566406 6.9765625 L 14.353516 7.8867188 L 14.353516 6.046875 L 8.7792969 6.046875 z M 9.7089844 12.398438 C 10.842574 13.192877 12.292191 13.192877 13.425781 12.398438 C 13.992571 12.797978 14.637433 13.015625 15.283203 13.015625 L 16.212891 13.015625 L 16.212891 13.945312 L 15.283203 13.945312 C 14.642073 13.945312 14.011161 13.782449 13.425781 13.480469 C 12.264321 14.084429 10.870444 14.084429 9.7089844 13.480469 C 9.1236041 13.782459 8.4860894 13.945312 7.8496094 13.945312 L 6.9199219 13.945312 L 6.9199219 13.015625 L 7.8496094 13.015625 C 8.4953794 13.015625 9.1421941 12.797977 9.7089844 12.398438 z '
        const color = '#ff0000'

        return {
          path: this.icons[`${marker.tip} - ${marker.culoare}`] ? this.icons[`${marker.tip} - ${marker.culoare}`].path : path,
          fillColor: this.icons[`${marker.tip} - ${marker.culoare}`] ? this.icons[`${marker.tip} - ${marker.culoare}`].color : color,
          fillOpacity: this.iconOpacity,
          anchor: { x: 11, y: 20 },
          strokeWeight: this.iconStrokeWeight ?? 1,
          scale: this.iconScale,
          labelOrigin: { x: this.labelX, y: this.labelY }
        }
      }
    }
  },

  methods: {
    decode (path) {
      if (path) {
        if (this.google) {
          return this.google.maps.geometry.encoding.decodePath(path)
        }
      } else {
        return null
      }
    },

    getCenter (poly) {
      if (this.google) {
        const path = this.decode(poly.path)

        const bounds = new this.google.maps.LatLngBounds()

        for (let i = 0; i < path.length; i++) {
          const point = new this.google.maps.LatLng(path[i].lat(), path[i].lng())
          bounds.extend(point)
        }

        return bounds.getCenter()
      } else {
        return null
      }
    },

    markerDragged ($event, marker) {
      const pos = { lat: $event.latLng.lat(), lng: $event.latLng.lng() }

      this.$set(marker, 'location', pos)
    },

    polyMarkerDragged ($event, poly) {
      const pos = { lat: $event.latLng.lat(), lng: $event.latLng.lng() }

      this.$set(poly, 'center', pos)
    },

    screenshot () {
      const mapNode = document.querySelector('.gm-style>div>div>div:last-child>div')
      console.log(mapNode)
      const transformMatrix = getComputedStyle(mapNode).transform.split(',')

      mapNode.setAttribute(
        'style',
        'left:' +
          parseFloat(transformMatrix[4]) +
          'px;top:' +
          parseFloat(transformMatrix[5]) +
          'px;transform: none;position: absolute;z-index: 983;'
      )

      html2canvas(this.$refs.map.$el, {
        backgroundColor: null,
        useCORS: true
      }).then(canvas => {
        canvas.toBlob(blob => {
          saveAs(blob, `administrare_piete_screenshot_${new Date().toLocaleString('ro-RO').replace(', ', '_')}.png`)
        })
      })
    },

    polyDragged ($event, poly, index) {
      const polyRef = this.$refs[`poly-${index}`][0]

      const path = polyRef.$polygonObject.getPath()
      const str = this.google.maps.geometry.encoding.encodePath(path)

      this.$emit('polypathchanged', { index, str })
    }
  }
}
</script>
